import {
  CUBE_AGGREGATE_INTERACTION_PRODUCT,
  CUBE_INSIGHT_PRODUCT,
  CUBE_INTERACTION_INDIVIDUAL_PRODUCT_ATTENDEE,
} from '@/api/hcp/interaction/types';
import type { CubeRawRow } from '@/utils/cube/mapper.type';
import { LogicalAndFilter, LogicalOrFilter } from '@cubejs-client/core';

const availableCubesForHierarchicalFilter = [
  CUBE_INTERACTION_INDIVIDUAL_PRODUCT_ATTENDEE,
  CUBE_AGGREGATE_INTERACTION_PRODUCT,
  CUBE_INSIGHT_PRODUCT,
];

export const therapeuticIndicationDimensions = (cube: (typeof availableCubesForHierarchicalFilter)[number]) => [
  `${cube}.global_therapeutic_area_name`,
  `${cube}.level_1_global_indication_name`,
  `${cube}.level_2_global_indication_name`,
  `${cube}.level_3_global_indication_name`,
  `${cube}.level_4_global_indication_name`,
];

export const applyHierarchicalFilter = (
  filter: LogicalAndFilter,
  values: string[],
  dimensions: string[] = therapeuticIndicationDimensions(CUBE_AGGREGATE_INTERACTION_PRODUCT),
  isWithAdditionalParam?: boolean,
  additionalParam?: string,
): void => {
  if (!values?.length) return;

  const additionalValue = additionalParam ? additionalParam : 'N/A';

  if (values.length === 1) {
    const selectedValue = values[0];
    const parts = selectedValue.split('__');

    parts.forEach((part, index) => {
      if (!part || !dimensions[index]) return;

      filter.and.push({
        member: dimensions[index],
        operator: 'equals',
        values: isWithAdditionalParam ? [part, additionalValue] : [part],
      });
    });
    return;
  }

  const orFilter: LogicalOrFilter = {
    or: [],
  };

  values.forEach(selectedValue => {
    const parts = selectedValue.split('__');

    const selectionFilter: LogicalAndFilter = {
      and: [],
    };

    parts.forEach((part, index) => {
      if (!part || !dimensions[index]) return;

      selectionFilter.and.push({
        member: dimensions[index],
        operator: 'equals',
        values: isWithAdditionalParam ? [part, additionalValue] : [part],
      });
    });

    if (selectionFilter.and.length > 0) {
      orFilter.or.push(selectionFilter);
    }
  });

  if (orFilter.or.length > 0) {
    filter.and.push(orFilter);
  }
};

export const mapHierarchicalResult = (
  value: CubeRawRow,
  dimensions: string[] = therapeuticIndicationDimensions(CUBE_AGGREGATE_INTERACTION_PRODUCT),
): string => {
  return dimensions
    .map((dimension: string) => value[dimension]?.toString())
    .filter(Boolean)
    .join('__');
};
